import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartData,
} from 'chart.js';
import { Box, Typography } from '@mui/material';
import { TP_COLORS } from '../../../../../constants';
import { getSubmissionsV2State } from '../../../../../store/V2/SubmissionsSlice';
import { useAppSelector } from '../../../../../hooks';

// Register chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

type TState = {
  labels: string[];
  averageTimes: number[];
};

const initialState: TState = {
  labels: [],
  averageTimes: [],
};

const TimeTakenChart: React.FC = () => {
  // State
  const [state, setState] = useState(initialState);

  // Store
  const { studentSubmissionsData } = useAppSelector(getSubmissionsV2State);

  const avgTimeTaken = (studentSubmissionsData?.totalAverageTime || 0) / 60000;

  useEffect(() => {
    if (studentSubmissionsData?.groupedSubmissions) {
      const topicMap = new Map<string, number>();

      studentSubmissionsData.groupedSubmissions.forEach((submission) => {
        topicMap.set(submission.topicName, submission.averageTime / 60000);
      });

      const labels = Array.from(topicMap.keys());
      const averageTimes = Array.from(topicMap.values());

      setState({
        labels,
        averageTimes,
      });
    }
  }, [studentSubmissionsData]);

  const data: ChartData<'line'> = {
    labels: state.labels,
    datasets: [
      {
        label: 'Time Taken (Minutes)',
        data: state.averageTimes,
        borderColor: 'transparent',
        backgroundColor: (context) => {
          const { ctx } = context.chart;
          const gradient = ctx.createLinearGradient(0, 0, 0, ctx.canvas.height);
          gradient.addColorStop(0, '#F33B09');
          gradient.addColorStop(0.3, 'rgba(171, 108, 0, 0.7368)');
          gradient.addColorStop(0.5, 'rgba(171, 111, 0, 0.5441)');
          gradient.addColorStop(0.7, 'rgba(214, 218, 8, 0.3467)');
          gradient.addColorStop(1, 'rgba(218, 215, 7, 0.06)');

          return gradient;
        },
        pointBackgroundColor: 'black', // Dots on the graph
        pointRadius: 5,
        fill: true,
        tension: 0,
      },
      {
        label: 'Average',
        data: Array(state.labels.length).fill(avgTimeTaken),
        borderColor: '#647488',
        borderWidth: 2,
        borderDash: [5, 5], // Dotted line for average
        pointRadius: 0,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem) => {
            const value = tooltipItem.raw;
            if (value < 1) {
              return `Time Taken: ${Math.round(value * 60)} sec`;
            }
            return `Time Taken: ${Math.round(value)} min`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Time Taken (Minutes)',
        },
        ticks: {
          callback: (value) => {
            if (value < 1) {
              return `${Math.round(value * 60)} sec`;
            }
            return `${value} min`;
          },
        },
      },
      x: {
        title: {
          display: true,
          text: 'Date / Attempt',
        },
      },
    },
  };
  return (
    <>
      <Box marginTop="16px">
        <Typography fontSize="18px" color={TP_COLORS.gray.darken}>
          Student Quiz Completion Timeline
        </Typography>
        <Typography fontSize="14px" color="#647488">
          Tracking Completion Time and Accuracy Across Topics
        </Typography>
      </Box>
      <Box sx={{ height: '400px', width: '100%', marginTop: '32px' }}>
        <Line data={data} options={options} />
      </Box>
    </>
  );
};

export default TimeTakenChart;
